/**
 * DashboardDataProvider - Unified data fetching for dashboard widgets
 * 
 * PERFORMANCE: 
 * - Single consolidated fetch for all dashboard data
 * - Promise.allSettled to prevent one widget blocking others
 * - Stale-while-revalidate caching for instant first paint
 * - Memoized context value to prevent re-renders
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSession } from "@/contexts/AuthSessionContext";

// Types for dashboard data
interface SolidStats {
  solid_count: number;
  solid_streak: number;
  highest_streak: number;
  trusted_score: number;
  prestige_tier: string;
}

interface LeaderboardEntry {
  user_id: string;
  full_name: string | null;
  solid_count: number;
  solid_streak: number;
  highest_streak: number;
  trusted_score: number;
  prestige_tier: string;
}

interface DashboardData {
  solidStats: SolidStats | null;
  solidLeaderboard: LeaderboardEntry[];
  flakeStats: {
    todayBails: number;
    weeklyBails: number;
    reliefMeter: number;
  } | null;
}

interface DashboardDataContextType {
  data: DashboardData;
  loading: boolean;
  error: Error | null;
  hasPartialData: boolean;
  refetch: () => Promise<void>;
}

const DEFAULT_DATA: DashboardData = {
  solidStats: null,
  solidLeaderboard: [],
  flakeStats: null,
};

const CACHE_KEY = 'dashboard_data_cache';
const CACHE_TTL = 30_000; // 30 seconds

// Load from localStorage cache
function getCache(): { data: DashboardData; timestamp: number } | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is still valid
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        return parsed;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCache(data: DashboardData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  } catch {
    // Ignore cache errors
  }
}

// Timeout wrapper to prevent hanging - placed outside for reuse
const FETCH_TIMEOUT = 5000; // 5 second timeout per project standards

const withTimeout = <T,>(promise: PromiseLike<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    Promise.resolve(promise),
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error("Timeout")), timeoutMs)
    ),
  ]);
};

const DashboardDataContext = createContext<DashboardDataContextType>({
  data: DEFAULT_DATA,
  loading: true,
  error: null,
  hasPartialData: false,
  refetch: async () => {},
});

export const useDashboardData = () => useContext(DashboardDataContext);

export const DashboardDataProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoadingAuth } = useAuthSession();
  const userIdRef = useRef<string | null>(user?.id ?? null);
  // Initialize from cache for instant first paint, but only if it belongs to the current user
  const cachedData = useMemo(() => {
    const c = getCache();
    if (!c) return null;
    // Cache is per-user; if there is no signed-in user, ignore it.
    if (!user?.id) return null;
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [data, setData] = useState<DashboardData>(cachedData?.data || DEFAULT_DATA);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<Error | null>(null);
  const [hasPartialData, setHasPartialData] = useState(false);

  const mountedRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Whenever the authenticated user id changes (sign-in switch, sign-out),
  // wipe the cache so previous user's data can never flash for a new user.
  useEffect(() => {
    const prev = userIdRef.current;
    const next = user?.id ?? null;
    if (prev !== next) {
      try { localStorage.removeItem(CACHE_KEY); } catch {}
      setData(DEFAULT_DATA);
      setHasPartialData(false);
      setError(null);
      userIdRef.current = next;
    }
  }, [user?.id]);


  const fetchDashboardData = useCallback(async () => {
    if (isFetchingRef.current || !mountedRef.current) return;
    isFetchingRef.current = true;

    try {
      // Never block waiting on auth; if auth isn't ready or user isn't authed, exit fast.
      if (isLoadingAuth || !user?.id) {
        if (mountedRef.current) {
          setData(DEFAULT_DATA);
          setLoading(false);
        }
        isFetchingRef.current = false;
        return;
      }

      // Using module-level withTimeout and FETCH_TIMEOUT constants

      // Fetch all data in parallel using Promise.allSettled with timeout
      const [solidStatsResult, leaderboardResult, flakeStatsResult] = await Promise.allSettled([
        // Solid stats for current user - wrap in Promise to ensure proper typing
        withTimeout(
          supabase
            .from("user_solids")
            .select("*")
            .eq("user_id", user.id)
            .maybeSingle()
            .then(r => r),
          FETCH_TIMEOUT
        ),
        
        // Solid leaderboard - wrap in Promise to ensure proper typing
        withTimeout(
          supabase
            .from("user_solids")
            .select("*")
            .order("solid_count", { ascending: false })
            .limit(20)
            .then(r => r),
          FETCH_TIMEOUT
        ),
        
        // Flake stats (counts)
        withTimeout(fetchFlakeStats(), FETCH_TIMEOUT),
      ]);

      if (!mountedRef.current) {
        isFetchingRef.current = false;
        return;
      }

      // Process results - use partial data on failures
      let failedCount = 0;
      const newData: DashboardData = { ...DEFAULT_DATA };

      // Solid stats
      if (solidStatsResult.status === 'fulfilled' && solidStatsResult.value.data) {
        newData.solidStats = solidStatsResult.value.data;
      } else if (solidStatsResult.status === 'fulfilled' && solidStatsResult.value.error?.code === 'PGRST116') {
        // No record found - use defaults
        newData.solidStats = {
          solid_count: 0,
          solid_streak: 0,
          highest_streak: 0,
          trusted_score: 0,
          prestige_tier: "none",
        };
      } else {
        failedCount++;
      }

      // Leaderboard
      if (leaderboardResult.status === 'fulfilled' && leaderboardResult.value.data) {
        // Enrich with names - but don't block on failures
        const entries = leaderboardResult.value.data;
        const userIds = entries.map((e: any) => e.user_id);
        
        try {
          // Add timeout to profile fetch to prevent hangs
          const profilesPromise = supabase
            .from("profiles_public")
            .select("id, full_name")
            .in("id", userIds);
          
          const { data: profiles } = await withTimeout(
            Promise.resolve(profilesPromise.then(r => r)),
            2000 // 2 second timeout for enrichment
          );

          const profileMap = new Map(profiles?.map((p: any) => [p.id, p.full_name]) || []);
          
          newData.solidLeaderboard = entries.map((e: any) => ({
            user_id: e.user_id,
            full_name: profileMap.get(e.user_id) || "Anonymous",
            solid_count: e.solid_count,
            solid_streak: e.solid_streak,
            highest_streak: e.highest_streak,
            trusted_score: Number(e.trusted_score),
            prestige_tier: e.prestige_tier,
          }));
        } catch {
          // Use entries without names
          newData.solidLeaderboard = entries.map((e: any) => ({
            user_id: e.user_id,
            full_name: "Anonymous",
            solid_count: e.solid_count,
            solid_streak: e.solid_streak,
            highest_streak: e.highest_streak,
            trusted_score: Number(e.trusted_score),
            prestige_tier: e.prestige_tier,
          }));
        }
      } else {
        failedCount++;
      }

      // Flake stats
      if (flakeStatsResult.status === 'fulfilled') {
        newData.flakeStats = flakeStatsResult.value;
      } else {
        failedCount++;
      }

      if (mountedRef.current) {
        setData(newData);
        setCache(newData);
        setHasPartialData(failedCount > 0);
        setLoading(false);
        setError(null);
      }

    } catch (err) {
      console.error("Dashboard data fetch error:", err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch dashboard data'));
        setLoading(false);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [isLoadingAuth, user?.id]);

  useEffect(() => {
    mountedRef.current = true;
    
    // If we had cached data, fetch fresh data in background
    if (cachedData) {
      fetchDashboardData();
    } else {
      setLoading(true);
      fetchDashboardData();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [fetchDashboardData, cachedData]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    data,
    loading,
    error,
    hasPartialData,
    refetch: fetchDashboardData,
  }), [data, loading, error, hasPartialData, fetchDashboardData]);

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
};

// Helper to fetch flake stats
async function fetchFlakeStats(): Promise<{
  todayBails: number;
  weeklyBails: number;
  reliefMeter: number;
}> {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
  
  // Calculate week start (Monday)
  const dayOfWeek = now.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToMonday).toISOString();
  const weekEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToMonday + 7).toISOString();

  const [todayResult, weeklyResult] = await Promise.all([
    supabase
      .from("invitations")
      .select("*", { count: "exact", head: true })
      .eq("has_cancelled", true)
      .gte("updated_at", todayStart)
      .lte("updated_at", todayEnd),
    supabase
      .from("invitations")
      .select("*", { count: "exact", head: true })
      .eq("has_cancelled", true)
      .gte("updated_at", weekStart)
      .lte("updated_at", weekEnd),
  ]);

  const todayBails = (todayResult.count || 0) + 23; // Base for social proof
  const weeklyBails = (weeklyResult.count || 0) + 142;
  const reliefMeter = Math.max(35, Math.min(100, Math.floor(((weeklyResult.count || 0) / 50) * 100)));

  return { todayBails, weeklyBails, reliefMeter };
}

export default DashboardDataProvider;
